import {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useReducer,
  isValidElement,
  Children,
  cloneElement
} from 'react'
import type { FC, ReactNode } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlay,
  faPause,
  faUndo,
  faVolumeUp,
  faVolumeOff
} from '@fortawesome/pro-light-svg-icons'
import { t } from 'ttag'

import { Highlighter } from '@h2/components/display/highlighter'
import { PB97T, PB50T } from '@h2/components/colors'

import { Controller, Provider, Events, Locales, Voices, VoicesGender } from './controller'
import type { ControllerOptions, BoundaryUpdate } from './controller'

enum Positions {
  TL = 'topLeft',
  TR = 'topRight',
  BL = 'bottomLeft',
  BR = 'bottomRight',
  TC = 'topCenter',
  RC = 'rightCenter',
  BC = 'bottomCenter',
  LC = 'leftCenter'
}
interface TextToSpeechProps {
  native?: boolean
  autoPlay?: boolean
  size?: 'small' | 'medium'
  position?: Positions
  align?: 'vertical' | 'horizontal'
  name?: Voices
  gender?: VoicesGender
  locale?: Locales
  markTextAsSpoken?: boolean
  onToggleAutoPlay?: (currentAutoPlay: boolean) => void
  highlightBgColor?: string
  highlightFgColor?: string
  children: ReactNode
}
interface State {
  boundary: BoundaryUpdate
  isPlaying: boolean
  isPaused: boolean
  isError: boolean
  isReady: boolean
}
interface Action {
  type: 'ready' | 'pause' | 'play' | 'boundary' | 'reset' | 'end' | 'error'
  payload?: BoundaryUpdate
}
interface MarkOptions {
  highlightBgColor?: string
  highlightFgColor?: string
}
type ControlsProps = Required<Pick<TextToSpeechProps, 'size' | 'position' | 'align'>>

const getGridStyles = ({ position }: { position: Positions }) => {
  let gridTemplateAreas = `'cnt ctl'`
  let gridTemplateColumns = '1fr auto'
  let alignItems = 'start'

  switch (position) {
    case Positions.TL:
      gridTemplateAreas = `'ctl cnt'`
      gridTemplateColumns = 'auto 1fr'
      break
    case Positions.BL:
      gridTemplateAreas = `'ctl cnt'`
      alignItems = 'end'
      gridTemplateColumns = 'auto 1fr'
      break
    case Positions.BR:
      gridTemplateAreas = `'cnt ctl'`
      alignItems = 'end'
      break
    case Positions.TC:
      gridTemplateColumns = '1fr'
      gridTemplateAreas = `'ctl'\n'cnt'`
      alignItems = 'center'
      break
    case Positions.RC:
      alignItems = 'center'
      break
    case Positions.BC:
      gridTemplateColumns = '1fr'
      gridTemplateAreas = `'cnt'\n'ctl'`
      alignItems = 'center'
      break
    case Positions.LC:
      gridTemplateAreas = `'ctl cnt'`
      gridTemplateColumns = 'auto 1fr'
      alignItems = 'center'
  }

  return {
    alignItems,
    gridTemplateAreas,
    gridTemplateColumns
  }
}
const Wrap = styled.div`
  display: grid;
  gap: 15px;
  ${getGridStyles};
`
const Control = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  background: white;
  border-radius: 15px;
  border-color: ${PB50T};
  border-style: solid;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Controls = styled.div<ControlsProps>`
  position: relative;
  grid-area: ctl;
  display: inline-flex;
  margin: ${({ position }) => (position.includes('Center') ? 'auto' : 0)};
  flex-direction: ${({ align }) => (align === 'vertical' ? 'column' : 'row')};
  gap: 3px;
  padding: ${({ size }) => (size === 'small' ? 0 : 5)}px;
  background-color: ${PB97T};
  color: ${PB50T};

  ${Control} {
    width: ${({ size }) => (size === 'small' ? 16 : 30)}px;
    height: ${({ size }) => (size === 'small' ? 16 : 30)}px;
    padding: ${({ size }) => (size === 'small' ? 0 : 3)}px;
    border-width: ${({ size }) => (size === 'small' ? 1 : 2)}px;

    > svg {
      ${({ size }) =>
        size === 'small' ? 'font-size: 0.5em; height: 11px' : 'height: 18px'};
    }
  }
`
const Content = styled.div`
  grid-area: cnt;
`
const normalizeWhiteSpace = (text: string[]) => {
  return text.join(' ').replace(/\s+/g, ' ').trim()
}
const isStringOrNumber = (value: ReactNode) => {
  return typeof value === 'string' || typeof value === 'number'
}
const markChildrenRecursively = (
  children: ReactNode,
  buffer: string[],
  boundary: BoundaryUpdate,
  options: MarkOptions
): ReactNode => {
  return Children.map(children, child => {
    let currentChild = child

    if (isValidElement(child)) {
      currentChild = cloneElement(child, {
        ...child.props,
        children: markChildrenRecursively(child.props.children, buffer, boundary, options)
      })
    }

    if (isStringOrNumber(child)) {
      const text = (child as string | number).toString()
      const { word, startChar, endChar } = boundary
      const bufferText = normalizeWhiteSpace(buffer)
      const normalText = normalizeWhiteSpace([text])
      const bufferTextLength = bufferText.length

      buffer.push(text)

      if (word) {
        const start = buffer.length
          ? // Add white-space char to buffer text length
            startChar - (bufferTextLength + 1)
          : startChar - bufferTextLength
        const end = endChar - bufferTextLength
        const prevText = normalText.substring(0, start)
        const foundText = normalText.substring(start, end)
        const afterText = normalText.substring(end, normalText.length)

        if (foundText) {
          return (
            <>
              {prevText}
              <Highlighter
                text={foundText}
                highlightBgColor={options.highlightBgColor}
                highlightFgColor={options.highlightFgColor}
                mark={foundText.replace(/[.!?,:]/, '').trim()}
              />
              {afterText}
            </>
          )
        }
      }
    }

    return currentChild
  })
}
const defaultBoundary = { word: '', startChar: 0, endChar: 0 }
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'pause':
      return { ...state, isPlaying: false, isPaused: true, isError: false }
    case 'play':
    case 'reset':
      return {
        ...state,
        isPlaying: true,
        isPaused: false,
        isError: false,
        boundary: defaultBoundary
      }
    case 'end':
      return {
        ...state,
        isPlaying: false,
        isPaused: false,
        isError: false,
        boundary: defaultBoundary
      }
    case 'error':
      return {
        ...state,
        isPlaying: false,
        isPaused: false,
        isError: true,
        boundary: defaultBoundary
      }
    case 'ready':
      return { ...state, isReady: true }
    case 'boundary':
      return {
        ...state,
        boundary: { ...state.boundary, ...(action.payload as BoundaryUpdate) }
      }
    default:
      throw new Error(`TextToSpeech: invalid action type ${action.type}`)
  }
}
const TextToSpeech: FC<TextToSpeechProps> = ({
  name,
  gender,
  locale,
  children,
  onToggleAutoPlay,
  native = false,
  autoPlay = false,
  size = 'medium',
  align = 'vertical',
  position = Positions.TR,
  highlightBgColor,
  highlightFgColor,
  markTextAsSpoken = false
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [state, dispatch] = useReducer(reducer, {
    boundary: defaultBoundary,
    isPlaying: false,
    isPaused: false,
    isError: false,
    isReady: false
  })
  const [markedChildren, spokenText] = useMemo(() => {
    const buff: string[] = []
    const options: MarkOptions = {}

    if (highlightBgColor) {
      options.highlightBgColor = highlightBgColor
    }

    if (highlightFgColor) {
      options.highlightFgColor = highlightFgColor
    }

    const marked = markChildrenRecursively(children, buff, state.boundary, options)

    return [marked, normalizeWhiteSpace(buff)]
  }, [children, highlightBgColor, highlightFgColor, state.boundary])
  const controller = useMemo(() => {
    const options: ControllerOptions = { locale: Locales.EN_US }

    if (name) {
      options.name = name
    }

    if (locale) {
      options.locale = locale
    }

    if (gender) {
      options.gender = gender
    }

    return new Controller(
      spokenText,
      native ? Provider.WEB_SPEECH_API : Provider.HTML_MEDIA_ELEMENT,
      options
    )
  }, [spokenText, gender, locale, native, name])
  const iconSize = useMemo(() => (size === 'small' ? 'xs' : 'sm'), [size])
  const handlePlay = useCallback(() => {
    if (state.isPaused) {
      controller.resume()
    } else {
      controller.play()
    }

    dispatch({ type: 'play' })
  }, [controller, state.isPaused])
  const handlePause = useCallback(() => {
    controller.pause()
    dispatch({ type: 'pause' })
  }, [controller])
  const handleReset = useCallback(() => {
    controller.reset()
    dispatch({ type: 'reset' })
  }, [controller])
  const handleAutoPlayToggle = useCallback(() => {
    if (state.isPlaying) {
      controller.clear()
      dispatch({ type: 'end' })
    }

    if (typeof onToggleAutoPlay === 'function') {
      onToggleAutoPlay(autoPlay)
    }
  }, [onToggleAutoPlay, autoPlay, state.isPlaying, controller])
  const handlePlayPause = useMemo(
    () => (state.isPlaying ? handlePause : handlePlay),
    [state.isPlaying, handlePause, handlePlay]
  )

  useEffect(() => {
    const onBeforeUnload = () => {
      controller.clear()
    }
    const handleEnd = () => {
      dispatch({ type: 'end' })
    }
    const handleReady = () => {
      dispatch({ type: 'ready' })

      if (autoPlay) {
        controller.play()
        dispatch({ type: 'play' })
      }
    }
    const handleError = () => {
      dispatch({ type: 'error' })
    }

    controller.addEventListener(Events.END, handleEnd)
    controller.addEventListener(Events.ERROR, handleError)
    controller.addEventListener(Events.READY, handleReady)
    window.addEventListener('beforeunload', onBeforeUnload)

    controller.init(
      !markTextAsSpoken
        ? undefined
        : boundary => {
            if (ref.current) {
              dispatch({ type: 'boundary', payload: boundary })
            }
          }
    )

    return () => {
      controller.clear()
      controller.removeEventListener(Events.END, handleEnd)
      controller.removeEventListener(Events.ERROR, handleError)
      controller.removeEventListener(Events.READY, handleReady)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [controller, autoPlay, markTextAsSpoken])

  return (
    <Wrap ref={ref} position={position}>
      <Controls align={align} size={size} position={position}>
        <Control onClick={handleAutoPlayToggle} title={t`Toggle audio`}>
          {state.isPlaying ? (
            <FontAwesomeIcon size={iconSize} icon={faVolumeUp} />
          ) : (
            <FontAwesomeIcon size={iconSize} icon={faVolumeOff} />
          )}
        </Control>
        <Control onClick={handlePlayPause} title={state.isPlaying ? t`Pause` : t`Play`}>
          {state.isPlaying ? (
            <FontAwesomeIcon size={iconSize} icon={faPause} />
          ) : (
            <FontAwesomeIcon size={iconSize} icon={faPlay} />
          )}
        </Control>
        {state.isPaused && (
          <Control onClick={handleReset} title={t`Restart`}>
            <FontAwesomeIcon size={iconSize} icon={faUndo} />
          </Control>
        )}
      </Controls>
      <Content>{markedChildren}</Content>
    </Wrap>
  )
}

export { TextToSpeech, Positions }
